import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DatepickerComponent } from '@modules/components/datepicker/datepicker.component';
import { BaseFilterItem } from '@modules/filter/directives/base-filter-item.directive';
import { FilterDateValidatorDirective } from '@modules/filter/directives/filter-date-validator.directive';
import { provideFilterItem } from '@modules/filter/utils/token';
import { FilterBean, FilterType } from '@type/filter.type';

const DEFAULT_FILTER = new FilterBean(FilterType.Date, { from: '', to: '', is_null: false, not_null: false });

@Component({
  selector: 'app-f-date',
  imports: [DatepickerComponent, FormsModule, ReactiveFormsModule, FilterDateValidatorDirective],
  providers: [provideFilterItem(FDateComponent)],
  templateUrl: './f-date.component.html',
  styleUrl: './f-date.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FDateComponent extends BaseFilterItem<FilterBean> {
  private date = inject(DatePipe);

  protected convertFilterToChips(): string[] {
    const { is_null, from, to, not_null } = this.filter;
    const chips: string[] = [];
    let str = '';

    if (from && to) {
      str = `${this.date.transform(from)} - ${this.date.transform(to)}`;
    }

    if (from && !to) {
      str = `≥ ${this.date.transform(from)}`;
    }

    if (!from && to) {
      str = `≤ ${this.date.transform(to)}`;
    }

    if (is_null) chips.push('Empty');
    if (not_null) chips.push('Filled');

    str ? chips.push(str.length > 12 ? str.substring(0, 12) + '...' : str) : '';
    return chips;
  }

  protected resetFilterItem(item: BaseFilterItem<FilterBean>): void {
    this.filter = this.getDefaultFilter();
    this.cdr.markForCheck();
  }

  protected getDefaultFilter(): FilterBean {
    return { ...DEFAULT_FILTER, name: this.filter?.name || '', model: this.filter?.model || '' };
  }

  protected processFilterForView(filter: FilterBean): void {
    this.filter.model = this.row().model;
    this.filter.name = this.row().name;
  }

  protected updateFilter(): void {
    this.filter.from = this.parseDate(this.filter.from);
    this.filter.to = this.parseDate(this.filter.to);
  }

  private getDate(dateLikeString: string): Date | null {
    const date = new Date(dateLikeString);
    return this.isDate(date) ? date : null;
  }

  private isDate(arg: any): arg is Date {
    return Object.prototype.toString.call(arg) === '[object Date]' && new Date(arg).toString() !== 'Invalid Date';
  }

  private getDateParams(date: Date) {
    const h = () => (date.getTimezoneOffset() / 60) * -1;
    return {
      y: date.getFullYear(),
      m: date.getMonth(),
      d: date.getDate(),
      h: h(),
    };
  }

  private parseDate(arg: any, offset: 0 | -1 = 0): string {
    const date = this.getDate(arg);
    if (date) {
      const { y, m, d, h } = this.getDateParams(date);
      return new Date(y, m, d - offset, h, 0, 0, offset).toISOString();
    } else {
      return '';
    }
  }

  protected getNecessaryProperties(filter: FilterBean) {
    return { is_null: filter.is_null, from: filter.from, to: filter.to, not_null: filter.not_null };
  }
}
